import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Loading } from "../components/Layout";
import { BookOpen, Upload, Trash2, Pencil, Eye, EyeOff, Save, X, FileText, User, Clock, HelpCircle, Search, Plus, ChevronDown, ChevronRight, Download } from "lucide-react";
import { GuideRenderer } from "../components/GuideRenderer";

const fmtDate = (s) => (s ? new Date(s).toLocaleString() : "");

function SourceBadge({ source }) {
  const map = {
    tenant: ["Your tenant's guide", "bg-[#3E8F99]/15 text-[#2c6b73]"],
    platform: ["Platform default", "bg-[#E2711D]/15 text-[#b1560f]"],
  };
  const [label, cls] = map[source] || ["Not set", "bg-[#1C2530]/10 text-[#1C2530]/50"];
  return <span data-testid={`help-source-${source || "none"}`} className={`font-mono text-[9px] uppercase tracking-wider px-2 py-0.5 ${cls}`}>{label}</span>;
}

const GuideContent = ({ content, compact }) => <GuideRenderer content={content} compact={compact} />;

function ManageCard({ slot, isSuper, onChange }) {
  const [open, setOpen] = useState(false);
  const [content, setContent] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();
  const role = slot.role;

  const loadContent = async () => {
    const { data } = await api.get(`/help/guides/${role}/content`);
    setContent(data.content || "");
    return data.content || "";
  };
  const toggleView = async () => {
    if (!open && content === null) await loadContent();
    setOpen((o) => !o);
  };
  const startEdit = async () => {
    const c = content === null ? await loadContent() : content;
    setDraft(c); setEditing(true); setOpen(true);
  };
  const upload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      await api.post(`/help/guides/${role}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${slot.role_label} guide uploaded`);
      setContent(null); setOpen(false); onChange();
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Upload failed"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const save = async () => {
    if (!draft.trim()) { toast.error("Guide content cannot be empty"); return; }
    setBusy(true);
    try {
      await api.put(`/help/guides/${role}`, { content: draft });
      toast.success(`${slot.role_label} guide saved`);
      setContent(draft); setEditing(false); onChange();
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Save failed"); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    const msg = isSuper ? "Remove this platform-default guide?" : "Remove your tenant's override and fall back to the platform default?";
    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      await api.delete(`/help/guides/${role}`);
      toast.success("Guide removed"); setContent(null); setOpen(false); setEditing(false); onChange();
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Remove failed"); }
    finally { setBusy(false); }
  };

  const hasOwn = isSuper ? slot.has_platform_default : slot.has_tenant_override;
  const cur = slot.current;

  const expanded = open || editing;
  const Btn = ({ onClick, disabled, title, testId, children, danger }) => (
    <button onClick={onClick} disabled={disabled} title={title} data-testid={testId}
      className={`flex items-center gap-1.5 border px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-wider disabled:opacity-30 ${danger ? "border-[#B23A2E]/40 text-[#B23A2E] hover:bg-[#B23A2E]/10" : "border-[#DFDAD0] text-[#1C2530]/80 hover:bg-[#EFECE5]"}`}>{children}</button>
  );

  return (
    <div className={`border bg-white flex flex-col ${expanded ? "col-span-full border-[#1C2530]/30" : "border-[#DFDAD0]"}`} data-testid={`help-manage-${role}`}>
      <div className="p-4 flex-1">
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-9 h-9 border border-[#DFDAD0] bg-[#EFECE5] flex items-center justify-center shrink-0"><FileText className="w-4 h-4 text-[#1C2530]/60" /></div>
            <div className="min-w-0">
              <div className="font-medium text-[#1C2530]">{slot.role_label}</div>
              <div className="text-xs text-[#1C2530]/50 truncate">{cur ? (cur.filename || cur.title) : "No guide yet"}</div>
            </div>
          </div>
          <SourceBadge source={slot.source} />
        </div>
        {cur ? (
          <dl className="grid grid-cols-2 gap-y-1.5 text-xs" data-testid={`help-card-meta-${role}`}>
            <dt className="text-[#1C2530]/45 flex items-center gap-1"><BookOpen className="w-3 h-3" /> Length</dt>
            <dd className="text-[#1C2530]/80 font-mono">{cur.chars?.toLocaleString()} chars</dd>
            {cur.updated_by && (<>
              <dt className="text-[#1C2530]/45 flex items-center gap-1"><User className="w-3 h-3" /> Updated by</dt>
              <dd className="text-[#1C2530]/80 truncate">{cur.updated_by}</dd>
              <dt className="text-[#1C2530]/45 flex items-center gap-1"><Clock className="w-3 h-3" /> When</dt>
              <dd className="text-[#1C2530]/80">{fmtDate(cur.updated_at)}</dd>
            </>)}
          </dl>
        ) : (
          <p className="text-xs text-[#1C2530]/50 italic">No guide set{!isSuper ? " and no platform default available" : ""}. Upload a file or write one to get started.</p>
        )}
        {!isSuper && !slot.has_tenant_override && slot.has_platform_default && (
          <p className="mt-2 text-[11px] text-[#b1560f]" data-testid={`help-fallback-note-${role}`}>Using the platform default — upload or edit to create your own version.</p>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-1.5 px-4 py-3 border-t border-[#DFDAD0] bg-[#EFECE5]">
        <Btn onClick={toggleView} disabled={!cur} title="View" testId={`help-view-${role}`}>{open ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}{open ? "Hide" : "View"}</Btn>
        <Btn onClick={startEdit} title="Edit" testId={`help-edit-${role}`}><Pencil className="w-3.5 h-3.5" /> Edit</Btn>
        <label title="Upload PDF/DOCX/PPTX" data-testid={`help-upload-${role}`}
          className="flex items-center gap-1.5 border border-[#DFDAD0] px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/80 hover:bg-[#EFECE5] cursor-pointer"><Upload className="w-3.5 h-3.5" /> Upload
          <input ref={fileRef} type="file" accept=".pdf,.docx,.pptx" onChange={upload} className="hidden" disabled={busy} /></label>
        <span className="flex-1" />
        <Btn onClick={remove} disabled={!hasOwn || busy} title={isSuper ? "Remove default" : "Remove override"} testId={`help-remove-${role}`} danger><Trash2 className="w-3.5 h-3.5" /></Btn>
      </div>
      {editing && (
        <div className="px-4 pb-4 pt-3 border-t border-[#DFDAD0]">
          <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={14} data-testid={`help-editor-${role}`}
            className="w-full border border-[#DFDAD0] p-3 text-sm font-mono outline-none focus:border-[#1C2530]" />
          <p className="text-[11px] text-[#1C2530]/50 mt-1">Tip: start section titles with a number ("3. My Queue"), and prefix a line with 🟢 🟡 🔴 ⚪ to show it as a status chip.</p>
          <div className="flex gap-2 mt-2">
            <button onClick={save} disabled={busy} data-testid={`help-save-${role}`}
              className="flex items-center gap-1.5 bg-[#1C2530] text-[#F5F3EE] px-4 py-2 text-sm hover:bg-[#2a3644] disabled:opacity-40"><Save className="w-3.5 h-3.5" /> Save</button>
            <button onClick={() => setEditing(false)} data-testid={`help-cancel-${role}`} className="flex items-center gap-1.5 border border-[#DFDAD0] px-4 py-2 text-sm hover:bg-[#EFECE5]"><X className="w-3.5 h-3.5" /> Cancel</button>
          </div>
        </div>
      )}
      {open && !editing && (<div className="px-4 pb-4 border-t border-[#DFDAD0] pt-4"><GuideContent content={content} /></div>)}
    </div>
  );
}

function FaqItem({ f, q, canManage, onEdit, onDelete }) {
  const [open, setOpen] = useState(!!q);
  useEffect(() => { if (q) setOpen(true); }, [q]);
  return (
    <div className="border border-[#DFDAD0] bg-white" data-testid={`faq-item-${f.id || f.q.slice(0, 20).replace(/\W+/g, "-")}`}>
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-[#EFECE5]">
        {open ? <ChevronDown className="w-4 h-4 text-[#1C2530]/50 mt-1 shrink-0" /> : <ChevronRight className="w-4 h-4 text-[#1C2530]/50 mt-1 shrink-0" />}
        <span className="font-medium text-[#1C2530] flex-1">{f.q}</span>
        {f.source === "tenant" && <span className="font-mono text-[9px] uppercase px-1.5 py-0.5 bg-[#3E8F99]/15 text-[#2c6b73] shrink-0">Your org</span>}
      </button>
      {open && (
        <div className="px-11 pb-4 text-sm text-[#1C2530]/85 leading-relaxed">
          {f.a}
          {canManage && f.source === "tenant" && (
            <div className="flex gap-2 mt-2">
              <button onClick={() => onEdit(f)} data-testid={`faq-edit-${f.id}`} className="font-mono text-[10px] uppercase text-[#3E8F99] hover:underline">Edit</button>
              <button onClick={() => onDelete(f)} data-testid={`faq-delete-${f.id}`} className="font-mono text-[10px] uppercase text-[#B23A2E] hover:underline">Delete</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function FaqEditor({ initial, onSaved, onClose }) {
  const [f, setF] = useState({ q: "", a: "", module: "/", ...initial });
  const save = async () => {
    try {
      if (f.id) await api.put(`/help/faqs/${f.id}`, f); else await api.post("/help/faqs", f);
      toast.success("FAQ saved"); onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Save failed"); }
  };
  return (
    <div className="border border-[#1C2530]/30 bg-white p-4 space-y-2" data-testid="faq-editor">
      <input value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} placeholder="Question" data-testid="faq-editor-q" className="w-full border border-[#DFDAD0] px-3 py-2 text-sm outline-none focus:border-[#1C2530]" />
      <textarea value={f.a} onChange={(e) => setF({ ...f, a: e.target.value })} rows={4} placeholder="Answer" data-testid="faq-editor-a" className="w-full border border-[#DFDAD0] px-3 py-2 text-sm outline-none focus:border-[#1C2530]" />
      <div className="flex gap-2">
        <button onClick={save} disabled={!f.q.trim() || !f.a.trim()} data-testid="faq-editor-save" className="flex items-center gap-1.5 bg-[#1C2530] text-[#F5F3EE] px-4 py-2 text-sm disabled:opacity-40"><Save className="w-3.5 h-3.5" /> Save</button>
        <button onClick={onClose} className="border border-[#DFDAD0] px-4 py-2 text-sm hover:bg-[#EFECE5]">Cancel</button>
      </div>
    </div>
  );
}

export default function Help() {
  const { user } = useAuth();
  const isSuper = user.role === "super_admin";
  const canManage = isSuper || user.role === "admin";
  const [guide, setGuide] = useState(null);
  const [tab, setTab] = useState("guide");
  const [faqQ, setFaqQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [showOverride, setShowOverride] = useState(false);
  const [slots, setSlots] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      const { data } = await api.get("/help/dynamic/pdf", { responseType: "blob" });
      const a = document.createElement("a"); a.href = URL.createObjectURL(data); a.download = `${guide.tenant} - ${guide.role_label} Guide.pdf`; a.click(); URL.revokeObjectURL(a.href);
    } catch { toast.error("PDF export failed"); } finally { setPdfBusy(false); }
  };

  const loadGuide = () => api.get("/help/dynamic").then((r) => setGuide(r.data)).catch(() => setGuide(false));
  const loadSlots = () => { if (canManage) api.get("/help/guides").then((r) => setSlots(r.data)).catch(() => setSlots([])); };
  useEffect(() => { loadGuide(); loadSlots(); }, []); // eslint-disable-line

  const fq = faqQ.trim().toLowerCase();
  const faqs = (guide?.faqs || []).filter((f) => !fq || f.q.toLowerCase().includes(fq) || f.a.toLowerCase().includes(fq));
  const delFaq = async (f) => { if (!window.confirm("Delete this FAQ?")) return; await api.delete(`/help/faqs/${f.id}`); toast.success("FAQ deleted"); loadGuide(); };

  return (
    <>
      <PageHeader eyebrow="HELP CENTER" title="Help & User Guide"
        right={guide && <span className="font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/50" data-testid="help-context">{guide.tenant} · {guide.role_label} · {guide.sections.length} topics</span>} />
      <div className="p-6 space-y-6 max-w-6xl">
        <div className="flex flex-wrap items-center gap-1 border-b border-[#DFDAD0]" data-testid="help-tabs">
          {[["guide", "Your guide", BookOpen], ["faq", "FAQ", HelpCircle], ["reader", "Reader view", FileText]].map(([k, l, I]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`help-tab-${k}`}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm border-b-2 -mb-px ${tab === k ? "border-[#B23A2E] text-[#1C2530] font-medium" : "border-transparent text-[#1C2530]/60 hover:text-[#1C2530]"}`}><I className="w-4 h-4" /> {l}</button>
          ))}
          <span className="flex-1" />
          <button onClick={downloadPdf} disabled={!guide || pdfBusy} data-testid="help-download-pdf" className="flex items-center gap-1.5 border border-[#DFDAD0] px-3 py-1.5 mb-1 font-mono text-[10px] uppercase tracking-wider hover:bg-[#EFECE5] disabled:opacity-40"><Download className="w-3.5 h-3.5" /> {pdfBusy ? "Preparing…" : "Download PDF"}</button>
        </div>

        {guide === null ? <Loading /> : guide === false ? <p className="text-sm text-[#B23A2E]">Could not load the guide.</p> : tab === "reader" ? (
          <article data-testid="help-reader" className="border border-[#DFDAD0] bg-white p-8 md:p-12 max-w-4xl space-y-6 leading-relaxed">
            <header className="border-b-2 border-[#B23A2E] pb-3">
              <h1 className="text-2xl font-semibold text-[#1C2530]">{guide.tenant} — {guide.role_label} Guide</h1>
              <p className="text-xs text-[#1C2530]/50 mt-1">{guide.sections.length} topics · {guide.faqs.length} questions · generated from your role's permissions</p>
            </header>
            {guide.sections.map((s) => (
              <section key={s.key} id={`reader-${s.key}`}>
                <h2 className="text-lg font-medium text-[#1C2530] mb-2"><span className="font-mono text-[11px] text-[#B23A2E] mr-2">{s.num}</span>{s.title}</h2>
                {s.lines.filter((l) => !l.startsWith("- ")).map((l, i) => <p key={i} className="text-sm text-[#1C2530]/85 my-1.5">{l.replace(/^[🟢🟡🔴⚪⚠]\s*/u, "")}</p>)}
                {s.lines.some((l) => l.startsWith("- ")) && <ul className="list-disc ml-5 space-y-1">{s.lines.filter((l) => l.startsWith("- ")).map((l, i) => <li key={i} className="text-sm text-[#1C2530]/85">{l.slice(2)}</li>)}</ul>}
              </section>
            ))}
            <section className="pt-6 border-t border-[#DFDAD0]">
              <h2 className="text-lg font-medium text-[#1C2530] mb-3">Frequently asked questions</h2>
              {guide.faqs.map((f, i) => <div key={i} className="mb-3"><div className="text-sm font-medium text-[#1C2530]">{f.q}</div><div className="text-sm text-[#1C2530]/80">{f.a}</div></div>)}
            </section>
          </article>
        ) : tab === "guide" ? (
          <section data-testid="help-my-guide" className="space-y-4">
            <p className="text-sm text-[#1C2530]/65" data-testid="help-dynamic-note">
              This guide is generated for <strong>{guide.role_label}</strong> at <strong>{guide.tenant}</strong> from your role's permissions — only the features you can actually use are shown.
            </p>
            {guide.override && (
              <div className="border border-[#3E8F99]/40 bg-[#3E8F99]/5 p-4" data-testid="help-override">
                <div className="flex items-center gap-2 mb-2"><SourceBadge source="tenant" /><span className="font-medium text-[#1C2530]">{guide.override.title || "Your organisation's custom guide"}</span></div>
                <GuideContent content={guide.override.content} compact />
              </div>
            )}
            <GuideContent content={guide.content} />
          </section>
        ) : (
          <section data-testid="help-faq" className="space-y-3">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              <div className="relative flex-1">
                <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-[#1C2530]/40" />
                <input value={faqQ} onChange={(e) => setFaqQ(e.target.value)} placeholder="Search questions…" data-testid="faq-search-input" className="w-full border border-[#DFDAD0] bg-white pl-7 pr-2 py-1.5 text-sm outline-none focus:border-[#1C2530]" />
              </div>
              {canManage && !isSuper && <button onClick={() => setEditing({})} data-testid="faq-add-btn" className="flex items-center gap-1.5 border border-[#DFDAD0] px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider hover:bg-[#EFECE5]"><Plus className="w-3.5 h-3.5" /> Add FAQ</button>}
            </div>
            {editing && <FaqEditor initial={editing} onSaved={() => { setEditing(null); loadGuide(); }} onClose={() => setEditing(null)} />}
            <div className="font-mono text-[9px] uppercase text-[#1C2530]/50" data-testid="faq-count">{faqs.length} question{faqs.length === 1 ? "" : "s"}</div>
            <div className="space-y-2">
              {faqs.length === 0 && <div className="text-sm text-[#1C2530]/50 italic py-6 text-center" data-testid="faq-empty">No questions match.</div>}
              {faqs.map((f, i) => <FaqItem key={f.id || i} f={f} q={fq} canManage={canManage} onEdit={setEditing} onDelete={delFaq} />)}
            </div>
          </section>
        )}

        {canManage && (
          <section className="pt-4 border-t border-[#DFDAD0]">
            <button onClick={() => setShowOverride((s) => !s)} data-testid="help-override-toggle" className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60 hover:text-[#1C2530]">
              {showOverride ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
              {isSuper ? "Platform default guide files (optional)" : "Custom guide override (optional)"}
            </button>
            {showOverride && (<>
              <p className="text-xs text-[#1C2530]/50 mt-2 mb-3">
                {isSuper ? "Optional uploaded guides shown to tenants in addition to the generated guide."
                  : "Upload or write an extra guide shown above the generated one for your organisation's users only. The generated guide always stays."}
              </p>
              {slots === null ? <Loading /> : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="help-manage-list">
                  {slots.map((s) => <ManageCard key={s.role} slot={s} isSuper={isSuper} onChange={() => { loadSlots(); loadGuide(); }} />)}
                </div>
              )}
            </>)}
          </section>
        )}
      </div>
    </>
  );
}
